import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ApplicationRef,
  type ComponentRef,
  computed,
  createComponent,
  effect,
  EnvironmentInjector,
  inject,
  Injectable,
  untracked,
} from '@angular/core';

import { AuthStateService } from '@arya/auth/data-access';
import type { DemoOutboxMessage } from '@arya/shared/dev-accounts';

import { firstValueFrom } from 'rxjs';

import { type AuthenticatorCodes, DEMO_AUTHENTICATOR_URL } from './demo-authenticator';
import { type DemoCodeKind, DemoCodeHint } from './demo-code-hint';
import { DemoOutbox } from './demo-outbox';

const CODE_FIELDS = 'input[autocomplete="one-time-code"]';

type MessageChannel = DemoOutboxMessage['channel'];

interface CodeField {
  readonly kind: DemoCodeKind;
  readonly channel?: MessageChannel;
}

const MESSAGE_CHANNELS: readonly MessageChannel[] = ['email', 'sms'];

const describe = (field: HTMLInputElement): CodeField => {
  const describedBy = field.getAttribute('aria-describedby') ?? '';
  if (describedBy.includes('totp')) return { kind: 'authenticator' };
  const channel = MESSAGE_CHANNELS.find((candidate) => describedBy.includes(candidate));
  return channel === undefined ? { kind: 'any' } : { kind: 'message', channel };
};

const latestCode = (codes: readonly DemoOutboxMessage[], { channel }: CodeField): string | undefined =>
  codes.find((message) => channel === undefined || message.channel === channel)?.code;

interface MountedHint {
  readonly ref: ComponentRef<DemoCodeHint>;
  readonly field: CodeField;
}

@Injectable({ providedIn: 'root' })
export class DemoCodeAssist {
  private readonly document = inject(DOCUMENT);
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);
  private readonly outbox = inject(DemoOutbox);
  private readonly http = inject(HttpClient);
  private readonly currentUser = inject(AuthStateService).currentUser;
  private readonly hints = new Map<HTMLInputElement, MountedHint>();
  private readonly codes = computed(() => this.outbox.messages().filter((message) => message.code !== undefined));

  constructor() {
    effect(() => {
      const codes = this.codes();
      for (const { ref, field } of this.hints.values()) {
        ref.setInput('code', latestCode(codes, field));
        ref.changeDetectorRef.detectChanges();
      }
    });
  }

  public start(): void {
    effect(
      (onCleanup) => {
        const observer = new MutationObserver(() => this.sync());
        observer.observe(this.document.body, { childList: true, subtree: true });
        untracked(() => this.sync());
        onCleanup(() => observer.disconnect());
      },
      { injector: this.injector }
    );
  }

  private sync(): void {
    for (const [field, { ref }] of this.hints) {
      if (!field.isConnected) this.unmount(field, ref);
    }
    for (const field of this.document.querySelectorAll<HTMLInputElement>(CODE_FIELDS)) {
      if (!this.hints.has(field)) this.mount(field);
    }
  }

  private mount(input: HTMLInputElement): void {
    const field = describe(input);
    const host = this.document.createElement('noxo-demo-code-hint');
    (input.closest('label') ?? input).insertAdjacentElement('afterend', host);
    const ref = createComponent(DemoCodeHint, { environmentInjector: this.injector, hostElement: host });
    ref.setInput('code', latestCode(untracked(this.codes), field));
    ref.setInput('kind', field.kind);
    ref.instance.fill.subscribe((code) => this.fillIn(input, code));
    ref.instance.fillAuthenticator.subscribe(() => void this.fillAuthenticator(input));
    this.appRef.attachView(ref.hostView);
    ref.changeDetectorRef.detectChanges();
    this.hints.set(input, { ref, field });
  }

  private unmount(field: HTMLInputElement, hint: ComponentRef<DemoCodeHint>): void {
    this.appRef.detachView(hint.hostView);
    hint.destroy();
    this.hints.delete(field);
  }

  private async fillAuthenticator(field: HTMLInputElement): Promise<void> {
    const { codes } = await firstValueFrom(this.http.get<AuthenticatorCodes>(DEMO_AUTHENTICATOR_URL));
    const email = this.currentUser()?.email;
    const code = codes.find((entry) => entry.email === email)?.code ?? codes[0]?.code;
    if (code !== undefined) this.fillIn(field, code);
  }

  private fillIn(field: HTMLInputElement, code: string): void {
    field.value = code;
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.focus();
  }
}
